import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  getSpeakersList,
  searchBlocks,
  replaceTextInBlocks,
  splitBlockBySelection,
} from '../utils/transcriptOperations';
import {
  findBlockIndex,
  findBlockElement,
  extractBlockIdFromElement,
  findBlockById,
  getSelectionCharacterOffsets,
} from '../utils/blockIdMatcher';
import BlockService from '../../../services/transcript/BlockService';
import TranscriptService from '../../../services/transcript/TranscriptService';
import useUndoRedo from './useUndoRedo';

/**
 * useTranscriptState Hook
 * 
 * Main state management for transcript editor
 * Combines block operations with undo/redo functionality
 * 
 * @param {Array} initialBlocks - Initial transcript blocks
 * @returns {Object} State and operation functions
 */
const useTranscriptState = (initialBlocks = [], sessionId = null) => {
  const safeInitialBlocks = Array.isArray(initialBlocks) ? initialBlocks : [];

  const {
    state: blocks,
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
  } = useUndoRedo(safeInitialBlocks);

  const [selectedBlocks, setSelectedBlocks] = useState([]);

  /**
   * Sync state when initialBlocks arrive (e.g. transcript loaded in background)
   * Only reset when transitioning from empty to loaded - avoids overwriting user edits
   */
  useEffect(() => {
    if (safeInitialBlocks.length > 0 && blocks.length === 0) {
      reset();
    }
  }, [safeInitialBlocks, blocks.length, reset]);

  const [focusedBlock, setFocusedBlock] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  /**
   * Get list of speakers
   */
  const speakers = useMemo(() => getSpeakersList(blocks), [blocks]);

  /**
   * Handle split block at cursor position
   */
  const handleSplit = useCallback(
    (blockId, cursorPosition, currentText = null) => {
      const result = BlockService.splitBlock(blocks, blockId, cursorPosition, currentText);
      if (result.blocks !== blocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle merge block with previous
   */
  const handleMergeUp = useCallback(
    (blockId) => {
      const result = BlockService.mergeUp(blocks, blockId);
      if (result.blocks !== blocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle merge block with next
   */
  const handleMergeDown = useCallback(
    (blockId) => {
      const result = BlockService.mergeDown(blocks, blockId);
      if (result.blocks !== blocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle insert block between two blocks
   */
  const handleInsertBlock = useCallback(
    (afterBlockId) => {
      const result = BlockService.insertBlockBetween(blocks, afterBlockId);
      if (result.blocks !== blocks && result.newBlockId) {
        pushState(result.blocks);
        // Note: Not auto-focusing the new block per user requirement
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle delete block
   */
  const handleDelete = useCallback(
    (blockId) => {
      const newBlocks = BlockService.delete(blocks, blockId);
      pushState(newBlocks);
      setSelectedBlocks(selectedBlocks.filter((id) => id !== blockId));
      if (focusedBlock === blockId) {
        setFocusedBlock(null);
      }
    },
    [blocks, selectedBlocks, focusedBlock, pushState]
  );

  /**
   * Handle duplicate block
   */
  const handleDuplicate = useCallback(
    (blockId) => {
      const result = BlockService.duplicate(blocks, blockId);
      if (result.blocks !== blocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle update block text
   */
  const handleUpdateText = useCallback(
    (blockId, newText, syncedWords = null) => {
      const newBlocks = BlockService.updateText(blocks, blockId, newText, syncedWords);
      pushState(newBlocks);
    },
    [blocks, pushState]
  );

  /**
   * Handle update block speaker
   */
  const handleUpdateSpeaker = useCallback(
    (blockId, newSpeaker) => {
      const newBlocks = BlockService.updateSpeaker(blocks, blockId, newSpeaker);
      pushState(newBlocks);
    },
    [blocks, pushState]
  );

  /**
   * Handle bulk update speakers for selected blocks
   */
  const handleBulkUpdateSpeaker = useCallback(
    (blockIds, newSpeaker) => {
      const newBlocks = BlockService.bulkUpdateSpeaker(blocks, blockIds, newSpeaker);
      pushState(newBlocks);
    },
    [blocks, pushState]
  );

  /**
   * Handle reorder blocks
   */
  const handleReorder = useCallback(
    (fromIndex, toIndex) => {
      const newBlocks = BlockService.reorder(blocks, fromIndex, toIndex);
      pushState(newBlocks);
    },
    [blocks, pushState]
  );

  /**
   * Handle move block up
   */
  const handleMoveUp = useCallback(
    (blockId) => {
      const newBlocks = BlockService.moveUp(blocks, blockId);
      if (newBlocks !== blocks) {
        pushState(newBlocks);
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle move block down
   */
  const handleMoveDown = useCallback(
    (blockId) => {
      const newBlocks = BlockService.moveDown(blocks, blockId);
      if (newBlocks !== blocks) {
        pushState(newBlocks);
      }
    },
    [blocks, pushState]
  );

  /**
   * Handle focus block
   */
  const handleFocusBlock = useCallback((blockId) => {
    setFocusedBlock(blockId);
  }, []);

  /**
   * Handle blur block
   */
  const handleBlurBlock = useCallback(() => {
    setFocusedBlock(null);
  }, []);

  /**
   * Handle select block
   */
  const handleSelectBlock = useCallback(
    (blockId, multiSelect = false) => {
      if (multiSelect) {
        setSelectedBlocks((prev) =>
          prev.includes(blockId)
            ? prev.filter((id) => id !== blockId)
            : [...prev, blockId]
        );
      } else {
        setSelectedBlocks([blockId]);
      }
    },
    []
  );

  /**
   * Handle select range of blocks
   */
  const handleSelectRange = useCallback(
    (fromBlockId, toBlockId) => {
      const fromIndex = findBlockIndex(blocks, fromBlockId);
      const toIndex = findBlockIndex(blocks, toBlockId);

      if (fromIndex === -1 || toIndex === -1) return;

      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);

      const rangeIds = blocks.slice(start, end + 1).map((block) => block.id);
      setSelectedBlocks(rangeIds);
    },
    [blocks]
  );

  /**
   * Handle clear selection
   */
  const handleClearSelection = useCallback(() => {
    setSelectedBlocks([]);
  }, []);

  /**
   * Handle search
   */
  const handleSearch = useCallback(
    (query, caseSensitive = false) => {
      setSearchQuery(query);
      const results = searchBlocks(blocks, query, caseSensitive);
      setSearchResults(results);
      setCurrentSearchIndex(0);
    },
    [blocks]
  );

  /**
   * Handle next search result
   */
  const handleNextSearch = useCallback(() => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
  }, [searchResults]);

  /**
   * Handle previous search result
   */
  const handlePreviousSearch = useCallback(() => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex(
      (prev) => (prev - 1 + searchResults.length) % searchResults.length
    );
  }, [searchResults]);

  /**
   * Handle replace text
   */
  const handleReplace = useCallback(
    (findText, replaceText, replaceAll = false, caseSensitive = false) => {
      const newBlocks = replaceTextInBlocks(
        blocks,
        findText,
        replaceText,
        replaceAll,
        caseSensitive
      );
      pushState(newBlocks);

      // Update search results
      if (searchQuery) {
        const results = searchBlocks(newBlocks, searchQuery, caseSensitive);
        setSearchResults(results);
      }
    },
    [blocks, searchQuery, pushState]
  );

  /**
   * Handle focus next block
   */
  const handleFocusNext = useCallback(() => {
    if (!focusedBlock) return;

    const blockIndex = findBlockIndex(blocks, focusedBlock);
    if (blockIndex === -1 || blockIndex >= blocks.length - 1) return;

    const nextBlock = blocks[blockIndex + 1];
    setFocusedBlock(nextBlock.id);

    // Trigger focus on DOM element (without scrolling)
    setTimeout(() => {
      const element = document.getElementById(`block-content-${nextBlock.id}`);
      element?.focus({ preventScroll: true });
    }, 0);
  }, [focusedBlock, blocks]);

  /**
   * Handle focus previous block
   */
  const handleFocusPrevious = useCallback(() => {
    if (!focusedBlock) return;

    const blockIndex = findBlockIndex(blocks, focusedBlock);
    if (blockIndex <= 0) return;

    const previousBlock = blocks[blockIndex - 1];
    setFocusedBlock(previousBlock.id);

    // Trigger focus on DOM element (without scrolling)
    setTimeout(() => {
      const element = document.getElementById(`block-content-${previousBlock.id}`);
      element?.focus({ preventScroll: true });
    }, 0);
  }, [focusedBlock, blocks]);

  /**
   * Handle add new speaker
   * If blocks are selected, assign new speaker to them
   * Otherwise, just confirm the speaker was added (will be available in dropdown)
   */
  const handleAddSpeaker = useCallback((newSpeakerName) => {
    if (!newSpeakerName || !newSpeakerName.trim()) {
      return { success: false, error: 'Speaker name is required' };
    }

    // Check if speaker already exists
    const existingSpeaker = speakers.find(s => 
      s.name.toLowerCase() === newSpeakerName.trim().toLowerCase()
    );
    
    if (existingSpeaker) {
      return { success: false, error: 'Speaker already exists' };
    }

    // If there are selected blocks, assign new speaker to them
    if (selectedBlocks.length > 0) {
      const updatedBlocks = blocks.map(block => {
        if (selectedBlocks.includes(block.id)) {
          return {
            ...block,
            speaker: newSpeakerName.trim(),
            metadata: {
              ...block.metadata,
              isEdited: true,
              lastModified: new Date().toISOString(),
            },
          };
        }
        return block;
      });
      
      pushState(updatedBlocks);
      return { 
        success: true, 
        message: `Speaker "${newSpeakerName}" added and assigned to ${selectedBlocks.length} block(s)`
      };
    }
    
    // If focused block exists, assign to it
    if (focusedBlock) {
      const updatedBlocks = blocks.map(block => {
        if (block.id === focusedBlock) {
          return {
            ...block,
            speaker: newSpeakerName.trim(),
            metadata: {
              ...block.metadata,
              isEdited: true,
              lastModified: new Date().toISOString(),
            },
          };
        }
        return block;
      });
      
      pushState(updatedBlocks);
      return { 
        success: true, 
        message: `Speaker "${newSpeakerName}" added and assigned to current block`
      };
    }

    // No blocks selected - create a new empty block with this speaker
    const result = BlockService.createEmptyBlock(blocks, newSpeakerName);
    pushState(result.blocks);
    
    // Focus the new block (without scrolling)
    setTimeout(() => {
      const element = document.getElementById(`block-content-${result.newBlockId}`);
      element?.focus({ preventScroll: true });
    }, 100);
    
    return { 
      success: true, 
      message: `Speaker "${newSpeakerName}" added with a new empty block. Start typing to add content.`
    };
  }, [speakers, selectedBlocks, focusedBlock, blocks, pushState]);

  /**
   * Handle rename speaker across all blocks
   */
  const handleRenameSpeaker = useCallback(async (oldName, newName) => {
    if (!sessionId) {
      throw new Error('Session ID required for renaming speaker');
    }

    const result = await TranscriptService.renameSpeaker(sessionId, oldName, newName);
    if (!result.success) {
      throw new Error(result.error);
    }
    
    // Update local blocks
    const updatedBlocks = blocks.map(block => 
      block.speaker === oldName ? { ...block, speaker: newName } : block
    );
    pushState(updatedBlocks);
  }, [sessionId, blocks, pushState]);

  /**
   * Handle delete speaker and merge to another
   */
  const handleDeleteSpeaker = useCallback(async (speakerName, mergeToSpeaker) => {
    if (!sessionId) {
      throw new Error('Session ID required for deleting speaker');
    }

    const result = await TranscriptService.deleteSpeaker(sessionId, speakerName, mergeToSpeaker);
    if (!result.success) {
      throw new Error(result.error);
    }
    
    // Update local blocks
    const updatedBlocks = blocks.map(block =>
      block.speaker === speakerName ? { ...block, speaker: mergeToSpeaker } : block
    );
    pushState(updatedBlocks);
  }, [sessionId, blocks, pushState]);

  /**
   * Handle replace all occurrences in all blocks
   */
  const handleReplaceAll = useCallback((searchText, replaceText) => {
    const { replaceAllInBlocks } = require('../utils/transcriptOperations');
    
    const { blocks: updatedBlocks, count } = replaceAllInBlocks(
      blocks,
      searchText,
      replaceText
    );

    if (count > 0) {
      pushState(updatedBlocks);
      return { success: true, count };
    }

    return { success: false, count: 0 };
  }, [blocks, pushState]);

  /**
   * Handle assign selected text (within one block) to a speaker.
   * Splits the block into before / selected / after; selected part gets newSpeaker.
   */
  const handleAssignSelectionToSpeaker = useCallback(
    (selection, newSpeaker) => {
      if (!selection || !selection.range || !selection.text || !newSpeaker || !newSpeaker.trim()) {
        return { success: false, error: 'Invalid selection or speaker' };
      }
      const startNode = selection.range.startContainer;
      const element = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode;
      const blockElement = findBlockElement(element);
      if (!blockElement) {
        return { success: false, error: 'Selection is not within a single block' };
      }
      const blockId = extractBlockIdFromElement(blockElement.id);
      if (!blockId) {
        return { success: false, error: 'Could not get block ID' };
      }
      const block = findBlockById(blocks, blockId);
      if (!block) {
        return { success: false, error: 'Block not found' };
      }
      const offsets = getSelectionCharacterOffsets(blockElement, selection.range);
      if (!offsets || offsets.startOffset >= offsets.endOffset) {
        return { success: false, error: 'Could not get selection range in block' };
      }
      const newBlocksArray = splitBlockBySelection(
        block,
        offsets.startOffset,
        offsets.endOffset,
        newSpeaker.trim()
      );
      const blockIdx = findBlockIndex(blocks, blockId);
      if (blockIdx === -1) {
        return { success: false, error: 'Block index not found' };
      }
      const updatedBlocks = [
        ...blocks.slice(0, blockIdx),
        ...newBlocksArray,
        ...blocks.slice(blockIdx + 1),
      ];
      pushState(updatedBlocks);
      return { success: true };
    },
    [blocks, pushState]
  );

  return {
    // State
    blocks,
    selectedBlocks,
    focusedBlock,
    speakers,
    searchQuery,
    searchResults,
    currentSearchIndex,

    // Undo/Redo
    undo,
    redo,
    canUndo,
    canRedo,

    // Block operations
    handleSplit,
    handleMergeUp,
    handleMergeDown,
    handleInsertBlock,
    handleDelete,
    handleDuplicate,
    handleUpdateText,
    handleUpdateSpeaker,
    handleBulkUpdateSpeaker,
    handleReorder,
    handleMoveUp,
    handleMoveDown,

    // Focus management
    handleFocusBlock,
    handleBlurBlock,
    handleFocusNext,
    handleFocusPrevious,

    // Selection management
    handleSelectBlock,
    handleSelectRange,
    handleClearSelection,

    // Search & Replace
    handleSearch,
    handleNextSearch,
    handlePreviousSearch,
    handleReplace,

    // Speaker management
    handleAddSpeaker,
    handleRenameSpeaker,
    handleDeleteSpeaker,

    // Text operations
    handleReplaceAll,
    handleAssignSelectionToSpeaker,
  };
};

export default useTranscriptState;


